import { useSyncExternalStore } from 'react';

import { API } from './EsConstants';
import { esPost, esRead } from './EsFetch';

/**
 * A table's voice in the browser (es-voice.md phase 2, voice_mode MESH): a peer connection to
 * every other seat in the campaign's room, the audio peer to peer and SRTP, and esback
 * carrying only the offers, answers, and ICE candidates between them on one WebSocket
 * (EsVoiceSocket). Six seats at most, the mesh's cap (§4).
 *
 * <p><b>One engine for the page</b>, module-level and read through useEsVoice, so the bar's
 * buttons and the Roster's rings see one room, and switching screens -- or the bar moving
 * between the screen bar and the console's -- never drops a call.
 *
 * <p><b>Join is the gesture</b> (§14, iOS Safari): {@link EsVoiceEngine#join} makes the
 * remote audio elements and the AudioContext synchronously inside the click, before anything
 * awaits, because Safari lets a page play only through an element a tap started. They are
 * pooled, one per possible peer, and a peer's stream is put into one when it arrives.
 *
 * <p><b>The quality line is the SDP's</b> (§4, §15): every description this side makes carries
 * Opus at {@code maxaveragebitrate=32000}, {@code usedtx=1}, and {@code useinbandfec=1}
 * (esOpusSdp), and each side reads the other's, so both directions are held to it. Mute is
 * {@code replaceTrack( null )} on every sender, so a muted seat sends nothing at all rather
 * than encoded silence, and the room is told, so every Roster shows the slashed mic.
 *
 * <p><b>The newcomer offers.</b> The socket's welcome lists the seats already bound; the seat
 * that just arrived calls each of them, and a seat already in waits to be called. Two seats
 * never offer to each other at once, so there is no glare to settle.
 *
 * <p>An observer (listenOnly) is never asked for the mic: it hears, and its transceiver sends
 * nothing until the GM lets it talk, and then its own Unmute is the press that asks.
 *
 * <p><b>The table's controls (phase 3) are honoured here, not enforced anywhere.</b> No server
 * hears the audio (§4), so a GM's hold is a message this browser obeys: a held seat's track
 * stays null and its Unmute is refused until the room says the hold is lifted (§14). Every
 * sender's track comes from one rule, trackFor: nothing while muted, and while whispering,
 * nothing to every seat but the one whispered to (§9, the track swap). The spotlight is each
 * listener's own volume: every other seat at a fifth.
 *
 * <p><b>Push-to-talk</b> is the account's (joined from the join's answer): the seat sits muted
 * and the held key, or the bar's Talk button held, unmutes it for as long as it is held. The
 * key is ignored while a field has focus, and a window that loses focus lets go.
 *
 * <p><b>Reconnect.</b> A peer whose ICE fails is restarted by the side that called it, with
 * fresh ICE servers, and hung up if it is not back in a minute (§9). A socket that drops is a
 * seat the room has already let go, so the seat joins again -- same mic, new peers -- backing
 * off for a minute before it gives up; the bar says so after ten seconds. A page hidden (a
 * phone backgrounded, which suspends its audio) tells the room, so the Roster says paused.
 *
 * <p>The pure halves -- esVoiceReduce, esOpusSdp, esRms, esPttKeyFor -- are exported for
 * EsVoice.test.jsx and EsVoiceControls.test.jsx, and createEsVoice takes the browser as a
 * parameter for the same reason.
 */

export const VOICE_MESH = 'MESH';
export const API_VOICE = API + 'voice';
export const API_VOICE_WS = API_VOICE + '/ws';
export const API_VOICE_PTT = API_VOICE + '/ptt';

export const VOICE_IDLE = 'idle';
export const VOICE_JOINING = 'joining';
export const VOICE_JOINED = 'joined';
export const VOICE_FAILED = 'failed';
export const VOICE_RECONNECTING = 'reconnecting';

export const MAX_SEATS = 6;

/** A table's room key, as esback names it: the chat's c<campaignId>. */
export const esVoiceRoomOf = ( pCampaignId ) => 'c' + pCampaignId;
export const PING_MS = 30000;

// Speaking: the RMS of a 20 ms window over this, and held this long after it falls, so a
// ring does not flicker between syllables (§9; AnalyserNode RMS, §15).
export const SPEAKING_RMS = 0.02;
export const SPEAKING_HOLD_MS = 300;
const METER_MS = 100;

// The spotlight: every seat but the one lit, at a fifth, at every listener (§9).
export const SPOTLIGHT_GAIN = 0.2;

// Reconnect (§9): the bar speaks up after ten seconds, and a minute is the end of trying,
// for a peer and for the socket alike. The socket backs off from one second, doubling.
export const RECONNECT_TELL_MS = 10000;
export const RECONNECT_GIVE_UP_MS = 60000;
export const REJOIN_FIRST_MS = 1000;
const WATCH_MS = 1000;

// The talk key when the account names none: esback's EsVoicePtt.DEFAULT_KEY.
export const PTT_DEFAULT_KEY = 'KeyV';

const OPUS_FMTP = { maxaveragebitrate: '32000', usedtx: '1', useinbandfec: '1' };

// The browser's own echo cancellation, noise suppression, and gain, each a switch in the
// device picker; a chosen input is added as an exact deviceId.
export const VOICE_SETTINGS = Object.freeze( {
    inputId: null, outputId: null, echoCancellation: true, noiseSuppression: true, autoGainControl: true,
} );

export const esVoiceConstraints = ( pSettings = VOICE_SETTINGS ) => ( {
    audio: {
        echoCancellation: pSettings.echoCancellation !== false,
        noiseSuppression: pSettings.noiseSuppression !== false,
        autoGainControl: pSettings.autoGainControl !== false,
        ...( pSettings.inputId ? { deviceId: { exact: pSettings.inputId } } : {} ),
    },
    video: false,
} );

// ============================================================ the pure halves

/**
 * The SDP with this table's Opus line: the three parameters added to Opus's fmtp, or set
 * where they are already there with another value, every other parameter kept. An SDP with
 * no Opus is returned as it came.
 */
export function esOpusSdp( pSdp ) {
    if ( typeof pSdp !== 'string' ) return pSdp;

    const aEnd = pSdp.includes( '\r\n' ) ? '\r\n' : '\n';
    const aLines = pSdp.split( aEnd );
    const aTypes = aLines
        .map( ( pLine ) => /^a=rtpmap:(\d+) opus\/48000/i.exec( pLine ) )
        .filter( Boolean )
        .map( ( pMatch ) => pMatch[ 1 ] );
    if ( !aTypes.length ) return pSdp;

    const fmtpOf = ( pType, pParams ) => {
        const aParams = new Map( pParams
            .split( ';' )
            .map( ( pPair ) => pPair.trim() )
            .filter( Boolean )
            .map( ( pPair ) => {
                const aAt = pPair.indexOf( '=' );
                return aAt < 0 ? [ pPair, '' ] : [ pPair.slice( 0, aAt ), pPair.slice( aAt + 1 ) ];
            } ) );
        Object.entries( OPUS_FMTP ).forEach( ( [ pKey, pValue ] ) => aParams.set( pKey, pValue ) );
        return 'a=fmtp:' + pType + ' ' + [ ...aParams ].map( ( [ pKey, pValue ] ) => pKey + '=' + pValue ).join( ';' );
    };

    const aOut = [];
    const aDone = new Set();
    aLines.forEach( ( pLine ) => {
        const aFmtp = /^a=fmtp:(\d+) (.*)$/.exec( pLine );
        if ( aFmtp && aTypes.includes( aFmtp[ 1 ] ) ) {
            aOut.push( fmtpOf( aFmtp[ 1 ], aFmtp[ 2 ] ) );
            aDone.add( aFmtp[ 1 ] );
            return;
        }
        aOut.push( pLine );
        const aMap = /^a=rtpmap:(\d+) opus\/48000/i.exec( pLine );
        // An Opus with no fmtp of its own gets one straight after its rtpmap.
        if ( aMap && !aLines.some( ( pOther ) => pOther.startsWith( 'a=fmtp:' + aMap[ 1 ] + ' ' ) ) ) {
            aOut.push( fmtpOf( aMap[ 1 ], '' ) );
            aDone.add( aMap[ 1 ] );
        }
    } );
    return aOut.join( aEnd );
}

/** The root mean square of one window of samples, -1..1. */
export function esRms( pSamples ) {
    if ( !pSamples?.length ) return 0;
    let aSum = 0;
    for ( let aAt = 0; aAt < pSamples.length; aAt++ ) aSum += pSamples[ aAt ] * pSamples[ aAt ];
    return Math.sqrt( aSum / pSamples.length );
}

export const VOICE_START = Object.freeze( {
    state: VOICE_IDLE, room: null, you: null, peers: {}, muted: false,
    speaking: {}, connection: {}, relay: false, error: null,
    ptt: { on: false, key: PTT_DEFAULT_KEY }, talking: false, spotlight: null, allHeld: false,
    whisperTo: null, whispers: {}, paused: false, notice: null, settings: VOICE_SETTINGS,
} );

// What the live region says next (§9: joins, leaves, and the GM's mute), numbered so the same
// sentence twice is still a change a screen reader announces.
const noticed = ( pState, pKind, pName ) => ( {
    ...pState, notice: { kind: pKind, name: pName ?? '', n: ( pState.notice?.n ?? 0 ) + 1 },
} );

/**
 * The room as this browser holds it, one event at a time. peers are the other bound seats by
 * seat id; speaking and connection are by seat id too, the local seat's included.
 */
export function esVoiceReduce( pState, pEvent ) {
    switch ( pEvent.type ) {
        case 'join':
            return { ...VOICE_START, state: VOICE_JOINING, room: pEvent.room };
        case 'joined':
            return {
                ...pState, you: pEvent.you, muted: !!pEvent.you?.muted, relay: !!pEvent.relay,
                ptt: { on: !!pEvent.ptt?.ptt, key: pEvent.ptt?.key || PTT_DEFAULT_KEY },
            };
        case 'welcome':
            return {
                ...pState, state: VOICE_JOINED, you: pEvent.you, muted: !!pEvent.you?.muted,
                peers: Object.fromEntries( ( pEvent.peers ?? [] ).map( ( pPeer ) => [ pPeer.seat, pPeer ] ) ),
                spotlight: pEvent.spotlight ?? null, allHeld: !!pEvent.allHeld,
                whisperTo: null, whispers: {}, error: null,
            };
        case 'peer-joined':
            return noticed( { ...pState, peers: { ...pState.peers, [ pEvent.peer.seat ]: pEvent.peer } },
                'joined', pEvent.peer.name );
        case 'peer-left': {
            const { [ pEvent.seat ]: aPeer, ...aPeers } = pState.peers;
            const { [ pEvent.seat ]: aSpoke, ...aSpeaking } = pState.speaking;
            const { [ pEvent.seat ]: aLink, ...aConnection } = pState.connection;
            const { [ pEvent.seat ]: aWhisper, ...aWhispers } = pState.whispers;
            if ( aPeer === undefined && aSpoke === undefined && aLink === undefined && aWhisper === undefined ) return pState;
            const aLeft = {
                ...pState, peers: aPeers, speaking: aSpeaking, connection: aConnection, whispers: aWhispers,
                spotlight: pState.spotlight === pEvent.seat ? null : pState.spotlight,
                whisperTo: pState.whisperTo === pEvent.seat ? null : pState.whisperTo,
            };
            return aPeer ? noticed( aLeft, 'left', aPeer.name ) : aLeft;
        }
        case 'peer-changed': {
            const aAllHeld = pEvent.allHeld === undefined ? pState.allHeld : !!pEvent.allHeld;
            if ( pEvent.peer.seat === pState.you?.seat ) {
                const aWas = pState.you;
                const aNext = {
                    ...pState, you: pEvent.peer, muted: !!pEvent.peer.muted, allHeld: aAllHeld,
                    talking: pEvent.peer.held ? false : pState.talking,
                };
                if ( pEvent.peer.held && !aWas.held ) return noticed( aNext, 'held' );
                if ( !pEvent.peer.held && aWas.held ) return noticed( aNext, 'lifted' );
                if ( !pEvent.peer.listenOnly && aWas.listenOnly ) return noticed( aNext, 'let-talk' );
                return aNext;
            }
            if ( !pState.peers[ pEvent.peer.seat ] ) return { ...pState, allHeld: aAllHeld };
            return { ...pState, allHeld: aAllHeld, peers: { ...pState.peers, [ pEvent.peer.seat ]: pEvent.peer } };
        }
        case 'spotlight':
            return pState.spotlight === ( pEvent.seat ?? null ) ? pState : { ...pState, spotlight: pEvent.seat ?? null };
        case 'whisper': {
            if ( pEvent.from === pState.you?.seat ) {
                const aTo = pEvent.on ? pEvent.to : ( pState.whisperTo === pEvent.to ? null : pState.whisperTo );
                return aTo === pState.whisperTo ? pState : { ...pState, whisperTo: aTo };
            }
            if ( pEvent.to !== pState.you?.seat ) return pState;
            const { [ pEvent.from ]: aWas, ...aOthers } = pState.whispers;
            const aNext = { ...pState, whispers: pEvent.on ? { ...aOthers, [ pEvent.from ]: true } : aOthers };
            const aName = pState.peers[ pEvent.from ]?.name;
            return pEvent.on && !aWas ? noticed( aNext, 'whisper', aName ) : aNext;
        }
        case 'talking':
            return !!pState.talking === !!pEvent.talking ? pState : { ...pState, talking: !!pEvent.talking };
        case 'paused':
            return !!pState.paused === !!pEvent.paused ? pState : { ...pState, paused: !!pEvent.paused };
        case 'settings':
            return { ...pState, settings: { ...pState.settings, ...pEvent.settings } };
        case 'reconnecting':
            return {
                ...pState, state: VOICE_RECONNECTING, peers: {}, speaking: {}, connection: {},
                whisperTo: null, whispers: {}, spotlight: null,
            };
        case 'notice':
            return noticed( pState, pEvent.kind, pEvent.name );
        case 'peer-muted':
            if ( pEvent.seat === pState.you?.seat ) {
                return { ...pState, muted: !!pEvent.muted, you: { ...pState.you, muted: !!pEvent.muted } };
            }
            if ( !pState.peers[ pEvent.seat ] ) return pState;
            return { ...pState, peers: { ...pState.peers, [ pEvent.seat ]: { ...pState.peers[ pEvent.seat ], muted: !!pEvent.muted } } };
        case 'mute':
            return { ...pState, muted: !!pEvent.muted };
        case 'speaking':
            if ( !!pState.speaking[ pEvent.seat ] === !!pEvent.speaking ) return pState;
            return { ...pState, speaking: { ...pState.speaking, [ pEvent.seat ]: !!pEvent.speaking } };
        case 'connection':
            return { ...pState, connection: { ...pState.connection, [ pEvent.seat ]: pEvent.connection } };
        case 'failed':
            return {
                ...VOICE_START, state: VOICE_FAILED, room: pState.room, error: pEvent.error ?? null,
                settings: pState.settings,
            };
        case 'left':
            return pState.settings === VOICE_SETTINGS ? VOICE_START : { ...VOICE_START, settings: pState.settings };
        default:
            return pState;
    }
}

/** Every seat in the room as this browser sees it, its own first. */
export const esVoiceSeats = ( pState ) =>
    ( pState.you ? [ pState.you ] : [] ).concat( Object.values( pState.peers ) );

/** The seat playing a character in this room, or null. */
export const esVoiceSeatFor = ( pState, pCharacterId ) =>
    pCharacterId == null ? null
        : esVoiceSeats( pState ).find( ( pSeat ) => pSeat.characterId === pCharacterId ) ?? null;

/** The GM's seat in the room, this browser's own included, or null. */
export const esVoiceGmSeat = ( pState ) => esVoiceSeats( pState ).find( ( pSeat ) => pSeat.gm ) ?? null;

/** Whether a voice is live here: joined, or holding its chair through a reconnect. */
export const esVoiceLive = ( pState ) => pState.state === VOICE_JOINED || pState.state === VOICE_RECONNECTING;

/**
 * Whether a key press is the talk key, and not typing: a field with focus has the key (§9),
 * and a key held with a modifier is somebody's shortcut, not a word to the table.
 */
export function esPttKeyFor( pEvent, pKey ) {
    if ( !pEvent || pEvent.code !== ( pKey || PTT_DEFAULT_KEY ) ) return false;
    if ( pEvent.ctrlKey || pEvent.metaKey || pEvent.altKey ) return false;

    const aTarget = pEvent.target;
    const aTag = String( aTarget?.tagName ?? '' ).toUpperCase();
    if ( aTag === 'INPUT' || aTag === 'TEXTAREA' || aTag === 'SELECT' ) return false;
    return !aTarget?.isContentEditable;
}

/**
 * The key as a person reads it: KeyV is V, Digit5 is 5, Space and the rest as the browser
 * names them. Not a copykey: a key's name is the same in every language here.
 */
export const esPttKeyLabel = ( pKey ) => String( pKey || PTT_DEFAULT_KEY ).replace( /^Key(?=[A-Z]$)/, '' ).replace( /^Digit(?=\d$)/, '' );

// ============================================================ the engine

/** The browser, as the engine reaches it; createEsVoice's default. */
export const esBrowserVoiceEnv = () => ( {
    post: ( pPath ) => esPost( pPath ),
    getUserMedia: ( pConstraints ) => navigator.mediaDevices.getUserMedia( pConstraints ),
    peerConnection: ( pConfig ) => new RTCPeerConnection( pConfig ),
    mediaStream: ( pTracks ) => new MediaStream( pTracks ),
    socket: ( pUrl ) => new WebSocket( pUrl ),
    socketUrl: () => ( window.location.protocol === 'https:' ? 'wss://' : 'ws://' ) + window.location.host + API_VOICE_WS,
    audioContext: () => {
        const aContext = window.AudioContext ?? window.webkitAudioContext;
        return aContext ? new aContext() : null;
    },
    audio: () => {
        const aAudio = document.createElement( 'audio' );
        aAudio.autoplay = true;
        aAudio.playsInline = true;
        aAudio.setAttribute( 'data-testid', 'es-voice-audio' );
        aAudio.className = 'sr-only';
        document.body.appendChild( aAudio );
        return aAudio;
    },
    setInterval: ( pRun, pMs ) => window.setInterval( pRun, pMs ),
    clearInterval: ( pId ) => window.clearInterval( pId ),
    setTimeout: ( pRun, pMs ) => window.setTimeout( pRun, pMs ),
    clearTimeout: ( pId ) => window.clearTimeout( pId ),
    now: () => Date.now(),
    read: ( pPath ) => esRead( pPath ),
    devices: () => navigator.mediaDevices?.enumerateDevices?.() ?? Promise.resolve( [] ),
    hidden: () => document.visibilityState === 'hidden',
    onVisibility: ( pRun ) => {
        document.addEventListener( 'visibilitychange', pRun );
        return () => document.removeEventListener( 'visibilitychange', pRun );
    },
} );

export class EsVoiceEngine {

    constructor( pEnv ) {
        this.env = pEnv;
        this.state = VOICE_START;
        this.listeners = new Set();
        this.peers = new Map();
        this.meters = new Map();
        this.pool = [];
        this.socket = null;
        this.stream = null;
        this.track = null;
        this.context = null;
        this.iceServers = [];
        this.timers = [];
        this.metering = null;
        this.watching = null;
        this.run = 0;
        this.rejoining = null;
        this.unwatchPage = null;

        this.subscribe = this.subscribe.bind( this );
        this.snapshot = this.snapshot.bind( this );
    }

    subscribe( pListener ) {
        this.listeners.add( pListener );
        return () => this.listeners.delete( pListener );
    }

    snapshot() {
        return this.state;
    }

    dispatch( pEvent ) {
        const aNext = esVoiceReduce( this.state, pEvent );
        if ( aNext === this.state ) return;
        this.state = aNext;
        this.listeners.forEach( ( pListener ) => pListener() );
    }

    /**
     * Join a room by its key (esVoiceRoomOf for a table's). CALL IT FROM THE CLICK HANDLER,
     * synchronously: the audio pool and the AudioContext are made before the first await, which
     * is what iOS Safari asks. Answers a promise that settles when the room has welcomed this
     * seat or the join failed.
     */
    join( pRoom ) {
        if ( this.state.state === VOICE_JOINING || esVoiceLive( this.state ) ) {
            if ( this.state.room === pRoom ) return Promise.resolve();
            this.leave();
        }

        this.pool = Array.from( { length: MAX_SEATS - 1 }, () => {
            const aAudio = this.env.audio();
            aAudio.play?.()?.catch?.( () => {} );
            return aAudio;
        } );
        this.context = this.env.audioContext();
        this.context?.resume?.()?.catch?.( () => {} );

        this.run += 1;
        this.dispatch( { type: 'join', room: pRoom } );
        this.watchPage();
        return this.start( pRoom, this.run );
    }

    async start( pRoom, pRun ) {
        const aCurrent = () => this.run === pRun;

        let aJoin;
        try {
            aJoin = await this.env.post( API_VOICE + '/' + pRoom + '/join' );
        }
        catch ( pError ) {
            if ( aCurrent() ) this.fail( pError?.message || 'join', false );
            return;
        }
        if ( !aCurrent() ) return;

        this.iceServers = aJoin?.iceServers ?? [];
        this.dispatch( { type: 'joined', you: aJoin?.you, relay: aJoin?.relay, ptt: aJoin?.ptt } );

        if ( !aJoin?.you?.listenOnly ) {
            try {
                this.stream = await this.env.getUserMedia( esVoiceConstraints( this.state.settings ) );
            }
            catch {
                if ( aCurrent() ) this.fail( 'mic', true );
                return;
            }
            if ( !aCurrent() ) {
                this.stream.getTracks().forEach( ( pTrack ) => pTrack.stop() );
                return;
            }
            this.track = this.stream.getAudioTracks()[ 0 ] ?? null;
            this.meter( aJoin.you.seat, this.stream, true );
        }

        this.open( pRoom, pRun );
    }

    open( pRoom, pRun ) {
        const aSocket = this.env.socket( this.env.socketUrl() );
        this.socket = aSocket;

        aSocket.onopen = () => {
            if ( this.run !== pRun ) return;
            this.send( { type: 'hello', room: pRoom } );
            this.timers.push( this.env.setInterval( () => this.send( { type: 'ping' } ), PING_MS ) );
        };
        aSocket.onmessage = ( pMessage ) => {
            if ( this.run !== pRun ) return;
            let aMessage;
            try {
                aMessage = JSON.parse( pMessage.data );
            }
            catch {
                return;
            }
            this.receive( aMessage );
        };
        aSocket.onclose = () => {
            if ( this.run !== pRun || this.socket !== aSocket ) return;
            // A seat the room welcomed rejoins; one that never got in has nothing to rejoin.
            if ( esVoiceLive( this.state ) ) this.rejoin();
            else this.fail( 'dropped', true );
        };
    }

    send( pMessage ) {
        if ( this.socket?.readyState === 1 ) this.socket.send( JSON.stringify( pMessage ) );
    }

    receive( pMessage ) {
        switch ( pMessage?.type ) {
            case 'welcome': {
                const aRejoined = this.rejoining;
                this.rejoining = null;
                this.dispatch( { type: 'welcome', you: pMessage.you, peers: pMessage.peers,
                                 spotlight: pMessage.spotlight, allHeld: pMessage.allHeld } );
                // Before any call goes out: push-to-talk sits muted between presses, and a seat
                // that was muted when its socket dropped comes back muted, though the room's
                // new chair says otherwise. The room is told either way.
                const aQuiet = ( this.state.ptt.on || aRejoined?.muted ) && !this.state.you?.listenOnly;
                if ( aQuiet && !this.state.muted ) this.mute( true );
                if ( aRejoined ) this.dispatch( { type: 'notice', kind: 'reconnected' } );
                ( pMessage.peers ?? [] ).forEach( ( pPeer ) => this.call( pPeer.seat ) );
                this.watch();
                if ( this.state.paused ) this.send( { type: 'pause', paused: true } );
                break;
            }
            case 'joined':
                if ( pMessage.peer ) this.dispatch( { type: 'peer-joined', peer: pMessage.peer } );
                break;
            case 'left':
                this.hangUp( pMessage.seat );
                this.dispatch( { type: 'peer-left', seat: pMessage.seat } );
                this.applyTracks();
                this.applyVolumes();
                break;
            case 'muted':
                this.dispatch( { type: 'peer-muted', seat: pMessage.seat, muted: pMessage.muted } );
                if ( pMessage.seat === this.state.you?.seat ) this.applyMute();
                break;
            case 'changed':
                if ( !pMessage.peer ) break;
                this.dispatch( { type: 'peer-changed', peer: pMessage.peer, allHeld: pMessage.allHeld } );
                if ( pMessage.peer.seat === this.state.you?.seat ) this.applyMute();
                break;
            case 'spotlight':
                this.dispatch( { type: 'spotlight', seat: pMessage.seat ?? null } );
                this.applyVolumes();
                break;
            case 'whisper':
                this.dispatch( { type: 'whisper', from: pMessage.from, to: pMessage.to, on: !!pMessage.on } );
                this.applyTracks();
                this.applyVolumes();
                break;
            case 'signal':
                this.signal( pMessage );
                break;
            default:
                break;
        }
    }

    peerFor( pSeat ) {
        const aWas = this.peers.get( pSeat );
        if ( aWas ) return aWas;

        const aPc = this.env.peerConnection( { iceServers: this.iceServers } );
        const aEntry = { pc: aPc, audio: this.pool.find( ( pAudio ) => pAudio.esSeat == null ) ?? null, pending: [],
                         caller: false, failedAt: null };
        if ( aEntry.audio ) aEntry.audio.esSeat = pSeat;
        this.peers.set( pSeat, aEntry );

        aPc.onicecandidate = ( pEvent ) => {
            if ( !pEvent.candidate ) return;
            const aCandidate = pEvent.candidate.toJSON ? pEvent.candidate.toJSON() : pEvent.candidate;
            this.send( { type: 'signal', to: pSeat, kind: 'candidate', candidate: aCandidate } );
        };
        aPc.ontrack = ( pEvent ) => {
            const aStream = pEvent.streams?.[ 0 ] ?? this.env.mediaStream( [ pEvent.track ] );
            if ( aEntry.audio ) {
                aEntry.audio.srcObject = aStream;
                aEntry.audio.play?.()?.catch?.( () => {} );
            }
            this.meter( pSeat, aStream, false );
            this.applyVolumes();
        };
        aPc.onconnectionstatechange = () => this.linked( pSeat, aEntry );
        return aEntry;
    }

    /** The track one seat is sent: nothing while muted, and while whispering, nothing but to the one whispered to. */
    trackFor( pSeat ) {
        if ( this.state.muted || !this.track ) return null;
        if ( this.state.whisperTo != null && pSeat !== this.state.whisperTo ) return null;
        return this.track;
    }

    /** The audio transceiver, sending the mic unless muted, listen-only, or whispering elsewhere. */
    async sendOn( pTransceiver, pSeat ) {
        pTransceiver.direction = 'sendrecv';
        await pTransceiver.sender.replaceTrack( this.trackFor( pSeat ) );
    }

    async call( pSeat, pRestart = false ) {
        const aEntry = this.peerFor( pSeat );
        aEntry.caller = true;
        try {
            if ( !pRestart ) await this.sendOn( aEntry.pc.addTransceiver( 'audio', { direction: 'sendrecv' } ), pSeat );
            const aOffer = await aEntry.pc.createOffer( pRestart ? { iceRestart: true } : undefined );
            const aLocal = { type: 'offer', sdp: esOpusSdp( aOffer.sdp ) };
            await aEntry.pc.setLocalDescription( aLocal );
            this.send( { type: 'signal', to: pSeat, kind: 'offer', sdp: aLocal.sdp } );
        }
        catch {
            this.dispatch( { type: 'connection', seat: pSeat, connection: 'failed' } );
        }
    }

    async signal( pMessage ) {
        const aSeat = pMessage.from;
        if ( aSeat == null ) return;

        try {
            if ( pMessage.kind === 'offer' ) {
                const aEntry = this.peerFor( aSeat );
                await aEntry.pc.setRemoteDescription( { type: 'offer', sdp: pMessage.sdp } );
                await this.flush( aEntry );
                const aTransceiver = aEntry.pc.getTransceivers()[ 0 ];
                if ( aTransceiver ) await this.sendOn( aTransceiver, aSeat );
                const aAnswer = await aEntry.pc.createAnswer();
                const aLocal = { type: 'answer', sdp: esOpusSdp( aAnswer.sdp ) };
                await aEntry.pc.setLocalDescription( aLocal );
                this.send( { type: 'signal', to: aSeat, kind: 'answer', sdp: aLocal.sdp } );
            }
            else if ( pMessage.kind === 'answer' ) {
                const aEntry = this.peers.get( aSeat );
                if ( !aEntry ) return;
                await aEntry.pc.setRemoteDescription( { type: 'answer', sdp: pMessage.sdp } );
                await this.flush( aEntry );
            }
            else if ( pMessage.kind === 'candidate' ) {
                const aEntry = this.peerFor( aSeat );
                if ( aEntry.pc.remoteDescription ) await aEntry.pc.addIceCandidate( pMessage.candidate );
                else aEntry.pending.push( pMessage.candidate );
            }
        }
        catch {
            this.dispatch( { type: 'connection', seat: aSeat, connection: 'failed' } );
        }
    }

    // Candidates that arrived before the description they belong to.
    async flush( pEntry ) {
        const aPending = pEntry.pending.splice( 0 );
        for ( const aCandidate of aPending ) await pEntry.pc.addIceCandidate( aCandidate );
    }

    // ---------------------------------------------------------------- reconnect

    /**
     * A peer's connection moved. Failed, the side that called restarts its ICE with fresh
     * servers -- the other side answers the new offer as it answered the first -- and the
     * watch hangs it up if it is not back within the minute. The answering side waits: two
     * restarts at once would be the glare the newcomer-offers rule exists to avoid.
     */
    linked( pSeat, pEntry ) {
        const aState = pEntry.pc.connectionState;
        if ( aState === 'connected' ) pEntry.failedAt = null;
        else if ( aState === 'failed' && pEntry.failedAt == null ) pEntry.failedAt = this.env.now();

        const aShown = aState === 'failed' && this.peers.get( pSeat ) === pEntry ? 'reconnecting' : aState;
        this.dispatch( { type: 'connection', seat: pSeat, connection: aShown } );
        if ( aState === 'failed' && pEntry.caller ) this.restart( pSeat, pEntry );
    }

    async restart( pSeat, pEntry ) {
        try {
            const aIce = await this.env.read?.( API_VOICE + '/' + this.state.room + '/ice' );
            if ( aIce?.iceServers ) {
                this.iceServers = aIce.iceServers;
                pEntry.pc.setConfiguration?.( { iceServers: this.iceServers } );
            }
        }
        catch {
            // The servers it has may still do; the restart is worth trying with them.
        }
        if ( this.peers.get( pSeat ) !== pEntry ) return;
        pEntry.pc.restartIce?.();
        await this.call( pSeat, true );
    }

    // Once a second while joined: a peer failed for a minute is hung up, and the bar is told
    // after ten seconds of one that is not back.
    watch() {
        if ( this.watching ) return;
        this.watching = this.env.setInterval( () => {
            const aNow = this.env.now();
            this.peers.forEach( ( pEntry, pSeat ) => {
                if ( pEntry.failedAt == null ) return;
                if ( aNow - pEntry.failedAt >= RECONNECT_GIVE_UP_MS ) {
                    this.hangUp( pSeat );
                    this.dispatch( { type: 'connection', seat: pSeat, connection: 'failed' } );
                    this.dispatch( { type: 'notice', kind: 'lost', name: this.state.peers[ pSeat ]?.name } );
                }
                else if ( !pEntry.told && aNow - pEntry.failedAt >= RECONNECT_TELL_MS ) {
                    pEntry.told = true;
                    this.dispatch( { type: 'notice', kind: 'peer-reconnecting', name: this.state.peers[ pSeat ]?.name } );
                }
            } );
        }, WATCH_MS );
        this.timers.push( this.watching );
    }

    /**
     * The socket dropped under a welcomed seat, and with it the seat: the room tells the others
     * it left. So it joins again -- the same mic, the same audio pool, new peers -- one second
     * after, then two, then four, until a minute has gone, and then it is a failure.
     */
    rejoin() {
        const aRoom = this.state.room;
        if ( !this.rejoining ) {
            this.rejoining = { since: this.env.now(), wait: REJOIN_FIRST_MS, told: false, muted: this.state.muted };
        }

        this.dropPeers();
        this.dispatch( { type: 'reconnecting' } );

        const aRejoin = this.rejoining;
        const aElapsed = this.env.now() - aRejoin.since;
        if ( aElapsed >= RECONNECT_GIVE_UP_MS ) {
            this.rejoining = null;
            this.fail( 'dropped', true );
            return;
        }

        this.run += 1;
        const aRun = this.run;
        const aWait = Math.min( aRejoin.wait, RECONNECT_GIVE_UP_MS - aElapsed );
        aRejoin.wait *= 2;

        this.timers.push( this.env.setTimeout( () => {
            if ( this.run !== aRun || this.rejoining !== aRejoin ) return;
            if ( !aRejoin.told && this.env.now() - aRejoin.since >= RECONNECT_TELL_MS ) {
                aRejoin.told = true;
                this.dispatch( { type: 'notice', kind: 'reconnecting' } );
            }
            void this.again( aRoom, aRun );
        }, aWait ) );
    }

    async again( pRoom, pRun ) {
        let aJoin;
        try {
            aJoin = await this.env.post( API_VOICE + '/' + pRoom + '/join' );
        }
        catch {
            if ( this.run === pRun ) this.rejoin();
            return;
        }
        if ( this.run !== pRun ) return;

        this.iceServers = aJoin?.iceServers ?? this.iceServers;
        this.open( pRoom, pRun );
    }

    // Every peer closed and the socket let go, keeping the mic, the pool, and the meters' own.
    dropPeers() {
        const aSocket = this.socket;
        this.socket = null;
        if ( aSocket ) {
            aSocket.onclose = null;
            aSocket.onmessage = null;
            aSocket.close?.( 1000 );
        }
        this.timers.forEach( ( pId ) => {
            if ( pId !== this.metering ) this.env.clearInterval( pId );
        } );
        this.timers = this.metering ? [ this.metering ] : [];
        this.watching = null;
        [ ...this.peers.keys() ].forEach( ( pSeat ) => this.hangUp( pSeat ) );
    }

    // ---------------------------------------------------------------- the seat's own controls

    /** Mute or unmute this seat: every sender's track swapped, and the room told. */
    mute( pMuted = !this.state.muted ) {
        if ( this.state.state !== VOICE_JOINED || this.state.you?.listenOnly ) return;
        // Held by the GM, the seat stays muted until the GM lifts it (§9).
        if ( !pMuted && this.state.you?.held ) return;
        if ( !pMuted && !this.track ) {
            this.takeMic();
            return;
        }
        this.dispatch( { type: 'mute', muted: pMuted } );
        this.applyMute();
        this.send( { type: 'mute', muted: pMuted } );
    }

    /**
     * The mic for a seat that joined without one: an observer the GM has let talk. Called from
     * the Unmute press, so a phone has its gesture.
     */
    async takeMic() {
        const aRun = this.run;
        try {
            const aStream = await this.env.getUserMedia( esVoiceConstraints( this.state.settings ) );
            if ( this.run !== aRun ) {
                aStream.getTracks().forEach( ( pTrack ) => pTrack.stop() );
                return;
            }
            this.stream = aStream;
            this.track = aStream.getAudioTracks()[ 0 ] ?? null;
            if ( this.state.you ) this.meter( this.state.you.seat, aStream, true );
            this.mute( false );
        }
        catch {
            this.dispatch( { type: 'notice', kind: 'mic' } );
        }
    }

    applyMute() {
        this.applyTracks();
        if ( this.state.muted && this.state.you ) this.dispatch( { type: 'speaking', seat: this.state.you.seat, speaking: false } );
    }

    // Every sender's track, by trackFor's one rule.
    applyTracks() {
        this.peers.forEach( ( pEntry, pSeat ) => {
            const aTrack = this.trackFor( pSeat );
            pEntry.pc.getSenders?.().forEach( ( pSender ) => {
                pSender.replaceTrack( aTrack )?.catch?.( () => {} );
            } );
        } );
    }

    // The spotlight at this listener: every seat but the lit one at a fifth. A seat whispering
    // to this one is never lowered, so the GM's aside is heard over the spotlight.
    applyVolumes() {
        const { spotlight, whispers } = this.state;
        this.peers.forEach( ( pEntry, pSeat ) => {
            if ( !pEntry.audio ) return;
            pEntry.audio.volume = spotlight == null || spotlight === pSeat || whispers[ pSeat ] ? 1 : SPOTLIGHT_GAIN;
        } );
    }

    /** Push-to-talk, the key or the bar's Talk button held: talking while held, muted after. */
    talk( pTalking ) {
        if ( !this.state.ptt.on || this.state.state !== VOICE_JOINED || this.state.you?.listenOnly ) return;
        if ( pTalking && this.state.you?.held ) return;
        if ( !!pTalking === !!this.state.talking ) return;

        this.dispatch( { type: 'talking', talking: pTalking } );
        this.mute( !pTalking );
    }

    /** A keydown or keyup anywhere on the page: the talk key, if it is the talk key. */
    pttKey( pEvent, pDown ) {
        if ( !this.state.ptt.on || !esPttKeyFor( pEvent, this.state.ptt.key ) ) return false;
        if ( pDown && pEvent.repeat ) return true;
        this.talk( pDown );
        return true;
    }

    // ---------------------------------------------------------------- the GM's controls

    /** The GM holds a seat muted, or lifts it. The room refuses anybody else. */
    hold( pSeat, pHeld ) {
        this.send( { type: 'hold', seat: pSeat, held: !!pHeld } );
    }

    holdAll( pHeld ) {
        this.send( { type: 'hold-all', held: !!pHeld } );
    }

    letTalk( pSeat, pTalk ) {
        this.send( { type: 'let-talk', seat: pSeat, talk: !!pTalk } );
    }

    spotlight( pSeat ) {
        this.send( { type: 'spotlight', seat: pSeat ?? null } );
    }

    /**
     * Whisper to one seat, or stop with null. The tracks swap at once, before the room's
     * answer: a word meant for one seat that reached the others while the frame travelled
     * would be the whole failure. The room's echo confirms it, and its refusal is silence --
     * a whisper the room will not carry is one this side never offers.
     */
    whisper( pSeat ) {
        if ( !esVoiceLive( this.state ) || !this.state.you ) return;
        const aWas = this.state.whisperTo;
        if ( pSeat != null ) this.dispatch( { type: 'whisper', from: this.state.you.seat, to: pSeat, on: true } );
        else if ( aWas != null ) this.dispatch( { type: 'whisper', from: this.state.you.seat, to: aWas, on: false } );
        this.applyTracks();
        this.send( pSeat == null ? { type: 'whisper' } : { type: 'whisper', to: pSeat } );
    }

    // ---------------------------------------------------------------- the page and the devices

    // The page hidden (§9, mobile): the room is told, so the Roster says paused; shown again,
    // the audio is started again, since a phone suspends it with the page.
    watchPage() {
        if ( this.unwatchPage || !this.env.onVisibility ) return;
        this.unwatchPage = this.env.onVisibility( () => this.paused( !!this.env.hidden?.() ) );
    }

    paused( pHidden ) {
        if ( !!pHidden === !!this.state.paused ) return;
        this.dispatch( { type: 'paused', paused: pHidden } );
        if ( pHidden ) this.talk( false );
        else {
            this.context?.resume?.()?.catch?.( () => {} );
            this.pool.forEach( ( pAudio ) => pAudio.play?.()?.catch?.( () => {} ) );
        }
        this.send( { type: 'pause', paused: !!pHidden } );
    }

    /** The microphones and speakers this browser offers, for the device picker. */
    async devices() {
        try {
            const aAll = await this.env.devices?.() ?? [];
            return {
                inputs: aAll.filter( ( pOne ) => pOne.kind === 'audioinput' ),
                outputs: aAll.filter( ( pOne ) => pOne.kind === 'audiooutput' ),
            };
        }
        catch {
            return { inputs: [], outputs: [] };
        }
    }

    /**
     * The device picker's choice (§9: input, output, EC, NS, AGC). A change to the input side
     * asks the browser for a new track and swaps it in on every sender with no renegotiation,
     * the old one stopped; the output is each audio element's sink, where the browser has one.
     */
    async configure( pSettings ) {
        const aWas = this.state.settings;
        this.dispatch( { type: 'settings', settings: pSettings } );
        const aNow = this.state.settings;

        if ( aNow.outputId !== aWas.outputId ) {
            this.pool.forEach( ( pAudio ) => pAudio.setSinkId?.( aNow.outputId ?? '' )?.catch?.( () => {} ) );
        }

        const aInput = [ 'inputId', 'echoCancellation', 'noiseSuppression', 'autoGainControl' ]
            .some( ( pKey ) => aNow[ pKey ] !== aWas[ pKey ] );
        if ( !aInput || !this.track || !this.state.you ) return;

        const aRun = this.run;
        let aStream;
        try {
            aStream = await this.env.getUserMedia( esVoiceConstraints( aNow ) );
        }
        catch {
            this.dispatch( { type: 'notice', kind: 'mic' } );
            return;
        }
        if ( this.run !== aRun ) {
            aStream.getTracks().forEach( ( pTrack ) => pTrack.stop() );
            return;
        }

        this.stream?.getTracks().forEach( ( pTrack ) => pTrack.stop() );
        this.stream = aStream;
        this.track = aStream.getAudioTracks()[ 0 ] ?? null;
        this.meters.delete( this.state.you.seat );
        this.meter( this.state.you.seat, aStream, true );
        this.applyTracks();
    }

    /** One seat's level, 0..1, as the meter last read it: read by a meter that polls, never held in state. */
    levelOf( pSeat ) {
        return this.meters.get( pSeat )?.level ?? 0;
    }

    // ---------------------------------------------------------------- leaving

    /** Leave the room: every connection closed, the mic released, esback told. */
    leave() {
        const aRoom = this.state.room;
        const aWasIn = this.state.state === VOICE_JOINING || esVoiceLive( this.state );
        this.teardown();
        this.dispatch( { type: 'left' } );
        if ( aWasIn && aRoom != null ) this.env.post( API_VOICE + '/' + aRoom + '/leave' )?.catch?.( () => {} );
    }

    fail( pError, pTellServer ) {
        const aRoom = this.state.room;
        this.teardown();
        this.dispatch( { type: 'failed', error: pError } );
        if ( pTellServer && aRoom != null ) this.env.post( API_VOICE + '/' + aRoom + '/leave' )?.catch?.( () => {} );
    }

    teardown() {
        this.run += 1;
        this.rejoining = null;
        this.timers.forEach( ( pId ) => {
            this.env.clearInterval( pId );
            this.env.clearTimeout?.( pId );
        } );
        this.timers = [];
        this.watching = null;
        this.unwatchPage?.();
        this.unwatchPage = null;

        const aSocket = this.socket;
        this.socket = null;
        if ( aSocket ) {
            aSocket.onclose = null;
            aSocket.onmessage = null;
            aSocket.close?.( 1000 );
        }

        this.peers.forEach( ( pEntry, pSeat ) => this.hangUp( pSeat ) );
        this.stream?.getTracks().forEach( ( pTrack ) => pTrack.stop() );
        this.stream = null;
        this.track = null;

        this.meters.clear();
        this.metering = null;
        this.pool.forEach( ( pAudio ) => {
            pAudio.pause?.();
            pAudio.srcObject = null;
            pAudio.remove?.();
        } );
        this.pool = [];
        this.context?.close?.()?.catch?.( () => {} );
        this.context = null;
    }

    hangUp( pSeat ) {
        const aEntry = this.peers.get( pSeat );
        if ( !aEntry ) return;
        this.peers.delete( pSeat );
        this.meters.delete( pSeat );
        aEntry.pc.close?.();
        if ( aEntry.audio ) {
            aEntry.audio.srcObject = null;
            aEntry.audio.volume = 1;
            aEntry.audio.esSeat = undefined;
        }
    }

    /**
     * A level meter on one stream. One interval reads every meter; a seat is speaking while its
     * RMS is over SPEAKING_RMS, and for SPEAKING_HOLD_MS after. The local seat reads silent
     * while muted: nobody hears it. The level itself is kept on the meter for levelOf.
     */
    meter( pSeat, pStream, pLocal ) {
        if ( !this.context?.createMediaStreamSource ) return;

        const aAnalyser = this.context.createAnalyser();
        aAnalyser.fftSize = 1024;
        this.context.createMediaStreamSource( pStream ).connect( aAnalyser );
        this.meters.set( pSeat, { analyser: aAnalyser, samples: new Float32Array( aAnalyser.fftSize ), loud: 0, local: pLocal, level: 0 } );

        if ( this.metering ) return;
        this.metering = this.env.setInterval( () => this.listen(), METER_MS );
        this.timers.push( this.metering );
    }

    listen() {
        const aNow = this.env.now();
        this.meters.forEach( ( pMeter, pSeat ) => {
            pMeter.analyser.getFloatTimeDomainData( pMeter.samples );
            pMeter.level = esRms( pMeter.samples );
            if ( pMeter.level > SPEAKING_RMS ) pMeter.loud = aNow;
            const aSpeaking = !( pMeter.local && this.state.muted ) && aNow - pMeter.loud < SPEAKING_HOLD_MS;
            this.dispatch( { type: 'speaking', seat: pSeat, speaking: aSpeaking } );
        } );
    }
}

/** An engine over the given browser, for a test; the page uses esVoice. */
export const createEsVoice = ( pEnv = esBrowserVoiceEnv() ) => new EsVoiceEngine( pEnv );

let page = null;

/** The page's one engine, made on first use so a page that never joins builds nothing. */
export function esVoice() {
    if ( !page ) page = createEsVoice();
    return page;
}

/** The room as this browser holds it, re-rendering on every change. */
export function useEsVoice() {
    const aEngine = esVoice();
    return useSyncExternalStore( aEngine.subscribe, aEngine.snapshot );
}
